<template>
  <div v-if="value" class="intro-modal-root" @keydown.esc.prevent="close">
    <div class="intro-modal-backdrop" @click="close" />
    <div
      class="intro-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intro-modal-title"
      @click.stop
    >
      <header class="intro-modal-header">
        <h2 id="intro-modal-title">{{ hasExisting ? 'Edit your intro' : 'Introduce yourself' }}</h2>
        <button type="button" class="intro-modal-close" aria-label="Close" @click="close">×</button>
      </header>
      <p class="intro-modal-lede">
        {{ isFaculty
          ? 'A little card so dancers can get to know you.'
          : 'A little card for the IABF community so other dancers can get to know you.' }}
      </p>

      <div class="intro-modal-body">
        <div class="intro-photo-row">
          <div class="intro-photo-preview">
            <img v-if="introHeadshotUrl" :src="introHeadshotUrl" alt="Your headshot">
            <span v-else>Photo</span>
          </div>
          <div class="intro-photo-actions">
            <p class="form-question" style="padding-top: 0;">Headshot (optional)</p>
            <input
              ref="headshotInput"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style="display:none"
              @change="onHeadshotChosen"
            >
            <button
              type="button"
              class="iabf-btn iabf-btn--secondary"
              :disabled="introPhotoUploading"
              @click="$refs.headshotInput && $refs.headshotInput.click()"
            >
              {{ introPhotoUploading ? 'Uploading…' : (introHeadshotUrl ? 'Replace photo' : 'Upload photo') }}
            </button>
            <button
              v-if="introHeadshotUrl"
              type="button"
              class="piece-link-btn"
              @click="introHeadshotUrl = ''; introDirty = true; introPhotoError = ''"
            >
              Remove
            </button>
            <p v-if="introPhotoError" class="photo-error" role="alert">{{ introPhotoError }}</p>
          </div>
        </div>

        <p class="form-question">How should your name appear?</p>
        <div class="feeling-chips" role="group" aria-label="Name display preference">
          <button
            v-for="opt in nameDisplayOptions"
            :key="opt.value"
            type="button"
            class="feeling-chip"
            :class="{ selected: introNameDisplayPreference === opt.value }"
            @click="setNameDisplayPreference(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <p v-if="namePreview" class="intro-statement">Preview: {{ namePreview }}</p>

        <p class="form-question">Where are you coming from? <span class="optional-tag">optional</span></p>
        <v-text-field
          v-model="introComingFrom"
          placeholder="City, state / country"
          maxlength="80"
          counter="80"
          @input="introDirty = true"
        />

        <p v-if="!isFaculty" class="form-question">How long have you been dancing? <span class="optional-tag">optional</span></p>
        <v-text-field
          v-if="!isFaculty"
          v-model="introYearsDancing"
          placeholder="e.g. 3 years · back after 20 years · since I was 8"
          maxlength="80"
          counter="80"
          @input="introDirty = true"
        />

        <div v-if="!isFaculty" class="intro-optional-block">
          <p class="form-question">Where are you staying during IABF?</p>
          <div class="show-hide-row" role="group" aria-label="Show stay on community card">
            <button
              type="button"
              class="show-hide-btn"
              :class="{ selected: introShowStay }"
              @click="setShowStay(true)"
            >
              Show
            </button>
            <button
              type="button"
              class="show-hide-btn"
              :class="{ selected: !introShowStay }"
              @click="setShowStay(false)"
            >
              Hide
            </button>
          </div>
          <div v-if="introShowStay" class="intro-optional-details">
            <p v-if="registrationStayKind" class="intro-statement">{{ stayStatement }}</p>
            <template v-else>
              <p class="feeling-hint">Your package lodging isn’t set yet — pick one for your card.</p>
              <div class="feeling-chips">
                <button
                  type="button"
                  class="feeling-chip"
                  :class="{ selected: introStayKind === 'onsite' }"
                  @click="setIntroStayKind('onsite')"
                >
                  Onsite
                </button>
                <button
                  type="button"
                  class="feeling-chip"
                  :class="{ selected: introStayKind === 'offsite' }"
                  @click="setIntroStayKind('offsite')"
                >
                  Offsite
                </button>
              </div>
            </template>
            <v-text-field
              v-if="effectiveStayKind"
              v-model="introHotel"
              :label="effectiveStayKind === 'onsite' ? 'Add detail (optional)' : 'Hotel name (optional)'"
              :placeholder="effectiveStayKind === 'onsite' ? 'e.g. Cottage' : 'e.g. SpringHill Suites New Smyrna'"
              maxlength="80"
              counter="80"
              @input="introDirty = true"
            />
          </div>
          <p v-else class="feeling-hint">Hidden from your community card.</p>
        </div>

        <div v-if="!isFaculty" class="intro-optional-block">
          <p class="form-question">Years at IABF?</p>
          <div class="show-hide-row" role="group" aria-label="Show years at IABF on community card">
            <button
              type="button"
              class="show-hide-btn"
              :class="{ selected: introShowYearsAtIabf }"
              @click="setShowYearsAtIabf(true)"
            >
              Show
            </button>
            <button
              type="button"
              class="show-hide-btn"
              :class="{ selected: !introShowYearsAtIabf }"
              @click="setShowYearsAtIabf(false)"
            >
              Hide
            </button>
          </div>
          <p v-if="introShowYearsAtIabf && yearsAtIabfStatement" class="intro-statement">
            {{ yearsAtIabfStatement }}
          </p>
          <p v-else-if="introShowYearsAtIabf" class="feeling-hint">
            We don’t have your attendance history yet — email hello@iabf.dance if this looks wrong.
          </p>
          <p v-else class="feeling-hint">Hidden from your community card.</p>
        </div>

        <p class="form-question">How are you feeling about IABF? <span class="optional-tag">optional</span></p>
        <p class="feeling-hint">Select all that apply.</p>
        <div class="feeling-chips">
          <button
            v-for="opt in feelingOptions"
            :key="opt"
            type="button"
            class="feeling-chip"
            :class="{ selected: introFeelings.includes(opt) }"
            @click="toggleFeeling(opt)"
          >
            {{ opt }}
          </button>
        </div>

        <p class="form-question">Anything else? <span class="optional-tag">optional</span></p>
        <p class="feeling-hint">One short line for the community card.</p>
        <v-textarea
          v-model="introNote"
          :placeholder="notePlaceholder"
          :counter="noteMax"
          :maxlength="noteMax"
          rows="2"
          auto-grow
          @input="introDirty = true"
        />

        <p v-if="introSuccess" class="form-success" role="status">{{ introSuccess }}</p>
      </div>

      <footer class="intro-modal-footer">
        <p v-if="introError" class="save-error" role="alert">{{ introError }}</p>
        <div class="intro-modal-footer-actions">
        <button type="button" class="iabf-btn iabf-btn--ghost" :disabled="introSaving" @click="close">
          Cancel
        </button>
        <button
          type="button"
          class="iabf-btn iabf-btn--primary"
          :disabled="introSaving"
          @click="saveIntro"
        >
          {{ introSaving ? 'Saving…' : 'Save introduction' }}
        </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { saveErrorMessage } from '@/utils/save-error'
import {
  INTRO_FEELING_OPTIONS,
  INTRO_MAX_HEADSHOT_BYTES,
  INTRO_MAX_NOTE,
  INTRO_NAME_DISPLAY_DEFAULT,
  INTRO_NAME_DISPLAY_OPTIONS,
  INTRO_NOTE_PLACEHOLDER,
  formatIntroDisplayName,
  introIsPublishable,
  normalizeIntroFeelings,
  normalizeNameDisplayPreference,
  stayKindFromRoom,
  ordinalYear
} from '@/config/iabf-intro'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import { IABF } from '@/config/iabf-collections'
import { isFacultyAccount, facultyYearsAtIabfNum } from '@/utils/iabf-faculty'
import { storage } from '@/firebase'

export default {
  name: 'IntroEditModal',
  props: {
    value: { type: Boolean, default: false }
  },
  data () {
    return {
      feelingOptions: INTRO_FEELING_OPTIONS,
      nameDisplayOptions: INTRO_NAME_DISPLAY_OPTIONS,
      notePlaceholder: INTRO_NOTE_PLACEHOLDER,
      noteMax: INTRO_MAX_NOTE,
      introComingFrom: '',
      introYearsDancing: '',
      introFeelings: [],
      introNote: '',
      introStayKind: '',
      introHotel: '',
      introShowStay: false,
      introShowYearsAtIabf: false,
      introNameDisplayPreference: INTRO_NAME_DISPLAY_DEFAULT,
      introHeadshotUrl: '',
      introDirty: false,
      introSaving: false,
      introPhotoUploading: false,
      introError: '',
      introPhotoError: '',
      introSuccess: '',
      hasExisting: false,
      loadToken: 0
    }
  },
  computed: {
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    person () {
      return this.$store.getters.currentIABFPerson || {}
    },
    dancerId () {
      return this.currentIABFDancer ? this.currentIABFDancer.id : null
    },
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) ||
        Number(this.currentIABFDancer && this.currentIABFDancer.festivalYear) ||
        IABF_DANCER_YEAR
    },
    isFaculty () {
      return isFacultyAccount(this.currentIABFDancer)
    },
    yearsAtIabfCount () {
      if (this.isFaculty) {
        const fromFaculty = facultyYearsAtIabfNum(
          this.currentIABFDancer && this.currentIABFDancer.yearsAtIabf
        )
        if (fromFaculty > 0) {
          return fromFaculty
        }
        const facultyYears = (this.person && this.person.yearsFaculty) ||
          (this.currentIABFDancer && this.currentIABFDancer.yearsFaculty) ||
          []
        const set = new Set((facultyYears || []).map(Number).filter(n => n > 0))
        if (this.festivalYear) {
          set.add(this.festivalYear)
        }
        return set.size
      }
      const years = this.person.yearsAttended ||
        (this.currentIABFDancer && this.currentIABFDancer.yearsAttended) ||
        []
      const set = new Set((years || []).map(Number).filter(n => n > 0))
      set.add(this.festivalYear)
      return set.size
    },
    yearsAtIabfStatement () {
      const ord = ordinalYear(this.yearsAtIabfCount)
      if (!ord) return ''
      return `This is my ${ord} year at IABF`
    },
    registrationStayKind () {
      const d = this.currentIABFDancer || {}
      return stayKindFromRoom(d.room) || ''
    },
    effectiveStayKind () {
      return this.registrationStayKind || this.introStayKind || ''
    },
    stayStatement () {
      const kind = this.effectiveStayKind
      if (kind === 'onsite') return 'I’m staying onsite'
      if (kind === 'offsite') return 'I’m staying offsite'
      return ''
    },
    namePreview () {
      const d = this.currentIABFDancer || {}
      return formatIntroDisplayName(d.firstName, d.lastName, this.introNameDisplayPreference)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (open) {
        if (open) {
          document.body.style.overflow = 'hidden'
          this.resetAndLoad()
        } else {
          document.body.style.overflow = ''
        }
      }
    }
  },
  beforeDestroy () {
    document.body.style.overflow = ''
  },
  methods: {
    close () {
      this.$emit('input', false)
      this.$emit('close')
    },
    async resetAndLoad () {
      this.introError = ''
      this.introPhotoError = ''
      this.introSuccess = ''
      this.introDirty = false
      this.hasExisting = false
      this.introComingFrom = ''
      this.introYearsDancing = ''
      this.introFeelings = []
      this.introNote = ''
      this.introStayKind = ''
      this.introHotel = ''
      this.introShowStay = false
      this.introShowYearsAtIabf = false
      this.introNameDisplayPreference = INTRO_NAME_DISPLAY_DEFAULT
      this.introHeadshotUrl = ''
      await this.ensureDancer()
      await this.loadIntro()
    },
    async ensureDancer () {
      if (this.dancerId) return
      // Don't load the admin’s own enrollment while viewing as someone else
      if (this.$store.state.masqueradeDancerId) return
      const user = this.$store.getters.user
      if (!user) return
      await this.$store.dispatch('loadIABFDancerByUid', user.id).catch(() => {})
      if (!this.dancerId && user.email) {
        await this.$store.dispatch('linkIABFDancerByEmail', {
          email: user.email,
          uid: user.id
        }).catch(() => {})
      }
    },
    async loadIntro () {
      if (!this.dancerId) return
      const token = ++this.loadToken
      try {
        const intro = await this.$store.dispatch('loadMyDancerIntro', { dancerId: this.dancerId })
        if (token !== this.loadToken) return
        if (intro) {
          this.hasExisting = introIsPublishable(intro) || !!(intro.comingFrom || intro.yearsDancing || intro.headshotUrl)
          this.introComingFrom = intro.comingFrom || ''
          this.introYearsDancing = intro.yearsDancing || ''
          this.introFeelings = normalizeIntroFeelings(intro.feeling)
          this.introNote = intro.note || ''
          this.introHeadshotUrl = intro.headshotUrl || ''
          this.introHotel = intro.hotel || ''
          this.introStayKind = intro.stayKind || ''
          this.introShowStay = intro.showStay === true
          this.introShowYearsAtIabf = intro.showYearsAtIabf === true
          this.introNameDisplayPreference = normalizeNameDisplayPreference(intro.nameDisplayPreference)
        }
        this.introDirty = false
      } catch (e) {
        console.warn('load intro failed', e)
      }
    },
    setNameDisplayPreference (value) {
      this.introNameDisplayPreference = normalizeNameDisplayPreference(value)
      this.introDirty = true
    },
    setShowStay (show) {
      this.introShowStay = !!show
      this.introDirty = true
    },
    setIntroStayKind (value) {
      this.introStayKind = value
      this.introDirty = true
    },
    setShowYearsAtIabf (show) {
      this.introShowYearsAtIabf = !!show
      this.introDirty = true
    },
    toggleFeeling (opt) {
      const next = this.introFeelings.slice()
      const i = next.indexOf(opt)
      if (i === -1) next.push(opt)
      else next.splice(i, 1)
      this.introFeelings = next
      this.introDirty = true
    },
    async onHeadshotChosen (event) {
      const file = event.target.files && event.target.files[0]
      if (this.$refs.headshotInput) this.$refs.headshotInput.value = ''
      if (!file || !this.dancerId) return
      this.introPhotoError = ''
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        this.introPhotoError = 'Please choose a JPG, PNG, or WebP image.'
        return
      }
      if (file.size > INTRO_MAX_HEADSHOT_BYTES) {
        this.introPhotoError = 'Photo must be under 25 MB.'
        return
      }
      this.introPhotoUploading = true
      try {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '')
        const cols = this.$store.getters.dancerIabf || IABF
        const prefix = (cols.storage && cols.storage.headshots) || IABF.storage.headshots
        const path = `${prefix}/${this.dancerId}-${Date.now()}.${ext || 'jpg'}`
        const ref = storage.ref(path)
        const snap = await ref.put(file, { contentType: file.type || 'image/jpeg' })
        this.introHeadshotUrl = await snap.ref.getDownloadURL()
        this.introDirty = true
      } catch (e) {
        console.error(e)
        this.introPhotoError = saveErrorMessage(e, 'Could not upload photo. Please try again.')
      }
      this.introPhotoUploading = false
    },
    async saveIntro () {
      this.introError = ''
      this.introSuccess = ''
      if (!this.dancerId) {
        this.introError = 'Hang tight — your registration is still loading.'
        return
      }
      // All fields optional — blank intros still save a community card with name preference
      this.introSaving = true
      try {
        const saved = await this.$store.dispatch('saveDancerIntro', {
          dancerId: this.dancerId,
          comingFrom: this.introComingFrom,
          yearsDancing: this.isFaculty ? '' : this.introYearsDancing,
          feeling: this.introFeelings,
          note: this.introNote,
          stayKind: this.isFaculty ? '' : this.effectiveStayKind,
          hotel: this.isFaculty ? '' : this.introHotel,
          showStay: this.isFaculty ? false : this.introShowStay,
          yearsAtIabf: this.yearsAtIabfCount,
          showYearsAtIabf: this.isFaculty ? this.yearsAtIabfCount > 0 : this.introShowYearsAtIabf,
          nameDisplayPreference: this.introNameDisplayPreference,
          headshotUrl: this.introHeadshotUrl
        })
        this.introDirty = false
        this.introSuccess = 'Saved!'
        this.$emit('saved', saved)
        // Brief pause so they see success, then close
        setTimeout(() => {
          this.close()
        }, 400)
      } catch (e) {
        const perm = e && (
          e.code === 'permission-denied' ||
          String((e && e.message) || '').toLowerCase().includes('permission')
        )
        if (perm && this.$store.state.masqueradeDancerId) {
          this.introError = 'Couldn’t save while viewing as this dancer. Try again — if it still fails, community-card permissions need a deploy.'
        } else {
          this.introError = saveErrorMessage(e)
        }
      }
      this.introSaving = false
    }
  }
}
</script>

<style scoped>
.intro-modal-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 12px;
  overflow-y: auto;
}
.intro-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 27, 31, 0.45);
}
.intro-modal {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 560px;
  margin: 12px auto 40px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 48px rgba(28, 27, 31, 0.18);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 48px);
}
.intro-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 0;
}
.intro-modal-header h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 24px;
  margin: 0;
  color: var(--iabf-ink, #1c1b1f);
  border: none;
  padding: 0;
}
.intro-modal-close {
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: var(--iabf-ink-faint, #8a8792);
  cursor: pointer;
  padding: 0 4px;
  margin: 0;
}
.intro-modal-lede {
  margin: 6px 20px 0;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.45;
}
.intro-modal-body {
  padding: 12px 20px 8px;
  overflow-y: auto;
  flex: 1;
}
.intro-modal-footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 12px 20px 18px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  flex-shrink: 0;
}
.intro-modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.save-error {
  margin: 0;
  color: #b54a4a !important;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.form-question {
  padding-top: 10px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0 0 6px;
}
.feeling-hint {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}
.optional-tag {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
}
.intro-photo-row {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 8px;
}
.intro-photo-preview {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--iabf-bg-soft, #eef0eb);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: var(--iabf-ink-faint, #8a8792);
  text-transform: uppercase;
}
.intro-photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.intro-photo-actions {
  min-width: 0;
  flex: 1;
}
.piece-link-btn {
  display: block;
  margin-top: 8px;
  border: none;
  background: none;
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  text-decoration: underline;
}
.feeling-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 8px;
}
.feeling-chip {
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  background: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  color: var(--iabf-ink-muted, #5c5a63);
  cursor: pointer;
  margin-top: 0;
}
.feeling-chip.selected {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.12);
  color: var(--iabf-ink, #1c1b1f);
}
.intro-optional-block {
  margin: 8px 0 14px;
}
.show-hide-row {
  display: flex;
  gap: 8px;
  margin: 4px 0 10px;
}
.show-hide-btn {
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  background: #fff;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 700;
  color: var(--iabf-ink-muted, #5c5a63);
  cursor: pointer;
  margin-top: 0;
}
.show-hide-btn.selected {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.14);
  color: var(--iabf-ink, #1c1b1f);
}
.intro-optional-details {
  padding: 12px 14px 4px;
  margin-top: 4px;
  background: var(--iabf-bg-soft, #eef0eb);
  border-radius: 12px;
}
.intro-statement {
  margin: 0 0 8px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--iabf-ink, #1c1b1f);
  line-height: 1.4;
}
.photo-error,
.form-error {
  color: #b54a4a !important;
  font-size: 14px;
  font-weight: 600;
  margin: 8px 0 0;
  line-height: 1.4;
}
.form-success {
  color: var(--iabf-success, #5f7a55);
  font-size: 14px;
  margin: 10px 0 0;
}
.iabf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  margin-top: 0;
}
.iabf-btn--primary {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}
.iabf-btn--secondary {
  background: #fff;
  color: var(--iabf-primary, #8a957d);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
}
.iabf-btn--ghost {
  background: transparent;
  color: var(--iabf-ink-muted, #5c5a63);
}
.iabf-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
